/**
 * SPA-side auth helpers.
 *
 * The browser never speaks OIDC directly — that flow lives entirely on
 * **insights-api**, which is the relying party. It moved there from the
 * dashboard's own Fastify backend; the property that the browser holds no IdP
 * token is unchanged, only the server keeping it. See
 * `docs/legacy/plans/dashboard-app-static.md`.
 *
 * The SPA only:
 *
 *   - Reads `GET /api/tenant/auth/me` once on first navigation to see whether a
 *     session exists, caching the answer in a reactive ref so header chrome can
 *     react.
 *   - Sends the user to `GET /api/tenant/auth/login?return_to=…` when an
 *     unauthenticated route is requested. insights-api handles the IdP
 *     round-trip and 302s back to `return_to`.
 *   - POSTs `/api/tenant/auth/logout` to destroy the session, then navigates to
 *     the IdP end-session URL when one was returned.
 *
 * Every call is **cross-origin with `credentials: "include"`** — the session
 * cookie lives on the insights-api origin, not this one.
 *
 * The cache is intentionally light: a process-wide `loaded` flag gates the
 * first fetch, and `logout()` resets it so the next navigation re-checks.
 */

import { computed, ref, type ComputedRef } from "vue";

import { getClientMe, type TenantRole } from "./api.ts";
import { CSRF_HEADER, INSIGHTS_API_URL } from "./env.ts";

/** Subset of session claims the SPA renders. Mirrors `/auth/me`. */
export interface CurrentUser {
  sub: string;
  email: string | null;
  name: string | null;
  preferred_username: string | null;
  /**
   * Tenant identifier — the `org` claim on the access_token, captured
   * at login. Null when the login-time lookup against insights-api
   * failed (e.g. transient outage); the organisation chip falls back to the
   * hard-coded "Dashboard" label in that case.
   */
  client_key: string | null;
  /**
   * Friendly client display label. Null when the registry row has no
   * `name` set — the organisation chip then falls back to `client_key`.
   */
  client_name: string | null;
}

/** Reactive handle on the current user — `null` while signed out. */
export const currentUser = ref<CurrentUser | null>(null);

/**
 * The caller's capability tier, from insights-api.
 *
 * Kept separate from {@link currentUser} because it comes from a
 * different place and means a different thing: `/auth/me` reports the
 * dashboard's own *session*, while the tier is projected from the
 * access_token insights-api actually verifies. Reading it from the API
 * rather than decoding the token here keeps one authority for the
 * answer.
 *
 * Starts at `"user"` — the fail-closed default, matching the API. The
 * window between page load and the lookup resolving therefore hides
 * authoring affordances rather than flashing them and taking them
 * away.
 */
export const tenantRole = ref<TenantRole>("user");

/**
 * Whether to show authoring affordances (create / edit / delete
 * insights, the data explorer).
 *
 * A hint for the UI only. Every route enforces the tier server-side,
 * so a stale value in a long-lived tab costs a 403, never access —
 * which is also why the SPA does not need to re-poll this.
 */
export const canEdit: ComputedRef<boolean> = computed(
  () => tenantRole.value === "editor",
);

let loaded = false;

/** Lazy fetch of `/api/tenant/auth/me`; idempotent for the page lifetime. */
export const loadCurrentUser = async (): Promise<CurrentUser | null> => {
  if (loaded) return currentUser.value;
  try {
    // Bare `fetch`, not `apiFetch`: a 401 here is the *expected* signed-out
    // answer that the router guard acts on, and routing it through the wrapper
    // would trigger an immediate redirect to login — turning "am I signed in?"
    // into "sign in now", and breaking the `/login` route itself.
    const r = await fetch(`${INSIGHTS_API_URL}/api/tenant/auth/me`, {
      credentials: "include",
    });
    if (r.ok) {
      currentUser.value = (await r.json()) as CurrentUser;
    } else {
      currentUser.value = null;
    }
  } catch {
    currentUser.value = null;
  }
  if (currentUser.value) {
    // Non-fatal: a failure here leaves the fail-closed `user` tier, so
    // the worst case is authoring affordances staying hidden for a
    // session. Showing them on a failed lookup would be the wrong
    // direction — the buttons would 403 on click.
    try {
      const me = await getClientMe();
      tenantRole.value = me.role;
    } catch {
      tenantRole.value = "user";
    }
  } else {
    tenantRole.value = "user";
  }
  loaded = true;
  return currentUser.value;
};

/**
 * Hand off to insights-api's `/api/tenant/auth/login`.
 *
 * `return_to` stays a root-relative path on *this* origin. The `//` guard is
 * kept even though insights-api validates the value again server-side
 * (`safeReturnTo`): a protocol-relative URL is treated as absolute by the
 * browser, and it costs nothing to refuse to send one in the first place.
 */
export const startLogin = (returnTo: string): void => {
  const url = new URL(`${INSIGHTS_API_URL}/api/tenant/auth/login`);
  if (returnTo && returnTo.startsWith("/") && !returnTo.startsWith("//")) {
    url.searchParams.set("return_to", returnTo);
  }
  window.location.assign(url.toString());
};

/**
 * Destroy the session on insights-api and follow the IdP's end-session URL
 * when one was returned.
 *
 * Always navigates — even on a network failure — so the user is never stranded
 * on a page whose session may already be gone.
 *
 * The CSRF header is sent even though `/api/tenant/auth/logout` sits outside
 * the cookie-auth zone and so isn't gated server-side. It still buys real
 * protection: a custom header forces the browser into a CORS preflight, and
 * insights-api only answers preflights from allow-listed origins. Without it, a
 * cross-origin `<form>` post could log a user out — minor, but free to prevent.
 */
export const logout = async (): Promise<void> => {
  let redirect = "/";
  try {
    const r = await fetch(`${INSIGHTS_API_URL}/api/tenant/auth/logout`, {
      method: "POST",
      credentials: "include",
      headers: { [CSRF_HEADER]: "1" },
    });
    if (r.ok) {
      // Shape is `{ ok, end_session_url }`; `end_session_url` is null when the
      // IdP advertises no `end_session_endpoint`, in which case staying on the
      // local fallback is correct — local logout has already succeeded.
      const data = (await r.json()) as { end_session_url?: string | null };
      if (typeof data.end_session_url === "string" && data.end_session_url) {
        redirect = data.end_session_url;
      }
    }
  } catch {
    // Network/parse failures still need to clear local state and
    // navigate — the cookie may already be gone if the server
    // accepted the request and then the parse failed.
  }
  currentUser.value = null;
  tenantRole.value = "user";
  loaded = false;
  window.location.assign(redirect);
};
